import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { startOfMinute } from 'date-fns';
import { combineLatest, Observable } from 'rxjs';
import { concatMap, filter, take, tap } from 'rxjs/operators';

import { IcebreakersStoreService } from '@libs/src/icebreakers-store/icebreakers-store.service';
import { OpportunityBackend } from '@libs/src/opportunity/opportunity.backend';
import { HubWindowService } from '@main-client/src/app/hub-window/hub-window.service';
import { ProfileStoreService } from '@main-client/src/app/profile/profile-store/profile-store.service';

import { fadeInEnter } from '@libs/src/animations/animations';
import {
  Account,
  AccountGroupMembership,
} from '@libs/src/models/account.model';
import { Group } from '@libs/src/models/group.model';
import { Tenant } from '@libs/src/tenant/tenant.interface';
import { Icebreaker } from '@main-client/src/app/icebreakers/icebreaker.model';
import { OPPORTUNITY_TYPES } from '@main-client/src/app/opportunity/opportunity-types.enum';
import { Opportunity } from '@main-client/src/app/opportunity/opportunity.model';
import { getReadableEducation } from '@main-client/src/app/pipes/account-readable-education-pipe/account-readable-education.helper';
import { Profile } from '@main-client/src/app/profile/profile';
import { LoadProfile } from '@main-client/src/app/profile/profile-store/profile.actions';
import { checkCanStartConversation } from '@main-client/src/app/profile/profile.helper';

import { IAppState } from '../../app.state';
import { TemplatePageTitleStrategy } from '../../core/template-page-title-strategy';

import get from 'lodash-es/get';
import includes from 'lodash-es/includes';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';

@Component({
  animations: [fadeInEnter],
  selector: 'app-profile-view',
  templateUrl: './profile-view.component.html',
})
export class ProfileViewComponent implements OnInit {
  icebreakers: Icebreaker[];
  isLoading = true;
  isUser = false;
  profileState = this.store.select('profile');
  opportunities: Opportunity[] = [];
  tenantState: Observable<Tenant> = this.store
    .select('tenant')
    .pipe(filter((tenant: Tenant) => !isEmpty(tenant)));
  userState = this.store.select('user');
  opportunitiesTotalCount = 0;
  showSections = {
    about: false,
    basicInfo: false,
    icebreakers: false,
  };
  canBeMessaged: boolean;
  firstGroup: Group;

  constructor(
    private templatePageTitleStrategy: TemplatePageTitleStrategy,
    private opportunityBackend: OpportunityBackend,
    private profileStoreService: ProfileStoreService,
    public route: ActivatedRoute,
    private store: Store<IAppState>,
    private icebreakersStoreService: IcebreakersStoreService,
    private hubWindowService: HubWindowService,
  ) {}

  ngOnInit() {
    this.route.params
      .pipe(
        concatMap((params: { profileSlug: string }) => {
          const userStateObservable = this.userState.pipe(
            filter((userState) => !!userState),
            take(1),
          );
          return combineLatest([
            this.profileStoreService.getProfileFromStore(params.profileSlug),
            userStateObservable,
            this.tenantState.pipe(take(1)),
            this.icebreakersStoreService.getIcebreakers(),
          ]);
        }),
        tap(([profile, userState, tenantState, icebreakersResponse]) => {
          this.firstGroup = get(userState, 'groups[0].document');
          this.isLoading = false;
          this.icebreakers = icebreakersResponse;
          this.store.dispatch(LoadProfile(profile));
          this.canBeMessaged = checkCanStartConversation(userState, profile);
          if (get(tenantState, 'enabled_features.events')) {
            this.getAccountOpportunities(profile, userState).subscribe();
          }
          this.isUser = isEqual(get(profile, '_id'), get(userState, '_id'));

          setTimeout(() => {
            this.templatePageTitleStrategy.updateTitle(null, profile.full_name);
          }, 0);
        }),
        tap(([profile]) => {
          this.showSections = {
            about: this.isUser || !!profile.about,
            basicInfo: this.isUser || this.showBasicInfo(profile),
            icebreakers:
              this.icebreakers.length &&
              (this.isUser || profile.icebreakers.length),
          };
        }),
      )
      .subscribe();
  }

  getAccountOpportunities(profile: Account, user: Account) {
    const eventStartDateMin = startOfMinute(new Date()).toISOString();
    const params = {
      eventStartDateMin,
      featuredAccountIds: profile._id,
      groupIds: this.getMutualGroupIds(profile, user),
      limit: '3',
      opportunityType: [OPPORTUNITY_TYPES.event, OPPORTUNITY_TYPES.exclusive],
      order: '1',
      skip: `${this.opportunities.length}`,
      sort: 'event_start_date',
    };
    return this.opportunityBackend.getOpportunities(params).pipe(
      tap((response: { items: Opportunity[]; total_count: number }) => {
        this.opportunities = [...this.opportunities, ...(response.items || [])];
        this.opportunitiesTotalCount = response.total_count;
      }),
    );
  }

  getMutualGroupIds(profile: Account, user: Account) {
    const userGroupIds = user.groups.map(
      (userGroup: AccountGroupMembership) => userGroup.document._id,
    );
    return profile.groups
      .filter((membership: AccountGroupMembership) =>
        includes(userGroupIds, membership.document._id),
      )
      .map((membership: AccountGroupMembership) => membership.document._id);
  }

  showBasicInfo(profile: Profile) {
    return (
      !!get(profile, 'social_accounts.linkedin') ||
      !!getReadableEducation(profile.education[0]) ||
      !!get(profile, 'geo_location[0].address') ||
      !!profile.website
    );
  }

  showPendingHubDialogue(result: {
    pendingGroup: Group;
    pendingMembership: AccountGroupMembership;
  }) {
    this.userState.pipe(take(1)).subscribe((user: Account) => {
      return this.hubWindowService.openWindow({
        membership: result.pendingMembership,
        group: result.pendingGroup,
        user,
      });
    });
  }
}
